import { Canvas } from "@react-three/fiber";
import { type DragEvent, useRef, useState } from "react";
import { type Camera, Plane, Raycaster, Vector2, Vector3 } from "three";
import { Scene } from "./scene/scene";
import { promoteToSpatial } from "./state/graph-actions";
import { seedTypeRegistry } from "./state/type-registry";
import { useUIStore } from "./state/ui-store";
import { useYDoc, YjsProvider } from "./state/yjs-provider";
import { Layout } from "./ui/layout";

export function App() {
  return (
    <YjsProvider>
      <AppShell />
    </YjsProvider>
  );
}

function AppShell() {
  const doc = useYDoc();
  seedTypeRegistry(doc);
  const cameraRef = useRef<Camera | null>(null);
  const [isCanvasDragOver, setIsCanvasDragOver] = useState(false);

  const handleCanvasDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isCanvasDragOver) setIsCanvasDragOver(true);
  };

  const handleCanvasDragLeave = () => {
    if (isCanvasDragOver) setIsCanvasDragOver(false);
  };

  const handleCanvasDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsCanvasDragOver(false);

    const blockId = event.dataTransfer.getData("application/x-vtt-block-id");
    if (!blockId || !cameraRef.current) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const ndc = new Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1,
    );

    const raycaster = new Raycaster();
    raycaster.setFromCamera(ndc, cameraRef.current);

    // Ground plane at y = 0 (scene uses XZ plane as tabletop).
    const hit = new Vector3();
    const groundPlane = new Plane(new Vector3(0, 1, 0), 0);
    const intersected = raycaster.ray.intersectPlane(groundPlane, hit);
    if (!intersected) return;

    promoteToSpatial(doc, blockId, {
      position: { x: hit.x, y: 0, z: hit.z },
    });
  };

  const viewMode = useUIStore((s) => s.viewMode);

  return (
    <div className="relative h-screen w-screen bg-[var(--color-bg-document)] overflow-hidden">
      <div
        className={`absolute inset-0 ${viewMode === "document" ? "hidden" : "block"} ${isCanvasDragOver ? "ring-1 ring-cyan-400/70" : ""}`}
        onDragOver={handleCanvasDragOver}
        onDragLeave={handleCanvasDragLeave}
        onDrop={handleCanvasDrop}
      >
        <Canvas
          camera={{ position: [0, 20, 0], fov: 50, near: 0.1, far: 1000 }}
          gl={{ antialias: true, alpha: false }}
          onCreated={({ gl, camera }) => {
            gl.setClearColor("#1a1a1a");
            cameraRef.current = camera;
          }}
        >
          <Scene />
        </Canvas>
      </div>
      <Layout />
    </div>
  );
}
